import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  runTransaction
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Header from '../Header';
import './JoinGame.css';

const JoinGame = () => {
  const { inviteCode } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [nickname, setNickname] = useState('');

  // Find game by invite code
  useEffect(() => {
    const findGame = async () => {
      try {
        setLoading(true);
        setError('');

        const gamesRef = collection(db, 'games');
        const q = query(gamesRef, where('inviteCode', '==', inviteCode.toUpperCase()));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setError('Invalid invite code. Please check and try again.');
          setLoading(false);
          return;
        }

        const gameDoc = querySnapshot.docs[0];
        const gameData = { id: gameDoc.id, ...gameDoc.data() };

        // Check if user is already in this game (check both teams)
        const inWhiteTeam = gameData.whiteTeam?.some(player => player.email === currentUser.email);
        const inBlackTeam = gameData.blackTeam?.some(player => player.email === currentUser.email);

        if (inWhiteTeam || inBlackTeam) {
          // User already in game, redirect to game
          navigate(`/game/${gameData.id}`);
          return;
        }

        setGame(gameData);
        setLoading(false);
      } catch (err) {
        console.error('Error finding game:', err);
        setError('Failed to find game. Please try again.');
        setLoading(false);
      }
    };

    if (inviteCode && currentUser) {
      findGame();
    }
  }, [inviteCode, currentUser, navigate]);

  const handleJoinGame = async () => {
    if (!game || !nickname.trim()) {
      setError('Please enter a nickname');
      return;
    }

    setJoining(true);
    setError('');

    try {
      const gameRef = doc(db, 'games', game.id);

      // Use transaction to ensure atomic team balancing
      await runTransaction(db, async (transaction) => {
        const gameDoc = await transaction.get(gameRef);

        if (!gameDoc.exists()) {
          throw new Error('Game not found');
        }

        const currentGame = gameDoc.data();

        // Check if player is already in a team
        const inWhiteTeam = currentGame.whiteTeam?.some(p => p.email === currentUser.email);
        const inBlackTeam = currentGame.blackTeam?.some(p => p.email === currentUser.email);

        if (inWhiteTeam || inBlackTeam) {
          // Player already in game, just navigate
          return;
        }

        // Create player object
        const playerData = {
          email: currentUser.email,
          nickname: nickname.trim(),
          role: null, // Will be assigned when game starts
          joinedAt: new Date().toISOString()
        };

        // Get current team counts (fresh data from transaction)
        const whiteCount = currentGame.whiteTeam?.length || 0;
        const blackCount = currentGame.blackTeam?.length || 0;

        // Determine which team to join
        let teamToJoin;
        if (whiteCount < blackCount) {
          teamToJoin = 'whiteTeam';
        } else if (blackCount < whiteCount) {
          teamToJoin = 'blackTeam';
        } else {
          // Equal teams, assign randomly
          teamToJoin = Math.random() < 0.5 ? 'whiteTeam' : 'blackTeam';
        }

        // Update the team with the new player
        const updatedTeam = [...(currentGame[teamToJoin] || []), playerData];
        transaction.update(gameRef, {
          [teamToJoin]: updatedTeam
        });
      });

      // Success! Navigate to the game
      navigate(`/game/${game.id}`);
    } catch (err) {
      console.error('Error joining game:', err);
      setError('Failed to join game. Please try again.');
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="join-container">
        <Header />
        <div className="join-card loading-card">
          <div className="loading-spinner"></div>
          <p>Finding game...</p>
        </div>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="join-container">
        <Header />
        <div className="join-card error-card">
          <div className="error-icon">⚠️</div>
          <h2>Game Not Found</h2>
          <p className="error-text">{error}</p>
          <button onClick={() => navigate('/game')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const whiteCount = game.whiteTeam?.length || 0;
  const blackCount = game.blackTeam?.length || 0;

  return (
    <div className="join-container">
      <Header />
      <div className="join-card">
        <div className="join-header">
          <div className="chess-icon-join">
            <svg viewBox="0 0 45 45" className="king-icon">
              <g fill="none" fillRule="evenodd" stroke="#6B8FFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 22.5,11.63 L 22.5,6" strokeLinejoin="miter"/>
                <path d="M 20,8 L 25,8" strokeLinejoin="miter"/>
                <path d="M 22.5,25 C 22.5,25 27,17.5 25.5,14.5 C 25.5,14.5 24.5,12 22.5,12 C 20.5,12 19.5,14.5 19.5,14.5 C 18,17.5 22.5,25 22.5,25"/>
                <path d="M 12.5,37 C 18,40.5 27,40.5 32.5,37 L 32.5,30 C 32.5,30 41.5,25.5 38.5,19.5 C 34.5,13 25,16 22.5,23.5 L 22.5,27 L 22.5,23.5 C 20,16 10.5,13 6.5,19.5 C 3.5,25.5 12.5,30 12.5,30 L 12.5,37"/>
                <path d="M 12.5,30 C 18,27 27,27 32.5,30" fill="none"/>
                <path d="M 12.5,33.5 C 18,30.5 27,30.5 32.5,33.5" fill="none"/>
                <path d="M 12.5,37 C 18,34 27,34 32.5,37" fill="none"/>
              </g>
            </svg>
          </div>
          <h1>Join Game</h1>
          <p className="game-name-join">{game.name}</p>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠</span>
            {error}
          </div>
        )}

        <div className="join-form">
          <div className="form-group">
            <label htmlFor="nickname">Your Nickname</label>
            <input
              type="text"
              id="nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Enter your chess nickname"
              maxLength={20}
              disabled={joining}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && nickname.trim()) {
                  handleJoinGame();
                }
              }}
            />
            <span className="help-text">This will be visible to other players</span>
          </div>

          <div className="game-info-box">
            <div className="info-row">
              <span className="info-label">Players Joined:</span>
              <span className="info-value">{whiteCount + blackCount} / {game.minPlayers}</span>
            </div>
            <div className="info-row">
              <span className="info-label">White Team:</span>
              <span className="info-value">{whiteCount} players</span>
            </div>
            <div className="info-row">
              <span className="info-label">Black Team:</span>
              <span className="info-value">{blackCount} players</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className="info-value">{game.status === 'waiting' ? 'Waiting for Players' : 'In Progress'}</span>
            </div>
          </div>

          <button
            onClick={handleJoinGame}
            className="join-submit-button"
            disabled={joining || !nickname.trim()}
          >
            {joining ? (
              <>
                <span className="loading-spinner-small"></span>
                Joining Game...
              </>
            ) : (
              'Join Game'
            )}
          </button>

          <button
            onClick={() => navigate('/game')}
            className="cancel-join-button"
            disabled={joining}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinGame;
